import React, { useEffect, useRef, useState } from "react";
import { MenuItem } from "@mui/material";
import * as yup from "yup";
import { Form } from "react-final-form";
import UiInput from "../../../_uiComponents/UiInput";
import { UiSelectInput } from "../../../_uiComponents/common/UiSelectInput";
import { UiButton } from "../../../_uiComponents/common/UiButton";
import Validator from "../../../../_helpers/validate.schema";
import { useDispatch, useSelector } from "react-redux";
import {
  clearForgotDetails,
  forgotPasswordOTPConfirmRequest,
  forgotPasswordOTPResendRequest,
  forgotPasswordValidateRequest,
  setForgotPasswordFormData,
} from "../../../../_redux/_actions";
import { useTranslation } from "react-i18next";
import { OtpConfirmModal } from "../../auth/views/OtpConfirmModal";
import { commonConstants } from "../../../../_redux/_constants";
import {
  getFingerPrint,
  validPassportNumber,
} from "../../../../_helpers/support.services";
import { formatNicNumberInput } from "../../../../../public/_helpers/support.services";

export const ForgotPasswordForm = (props) => {
  const { t, i18n } = useTranslation();
  const { onDirtyChange } = props;

  const validateSchema = () =>
    yup.object().shape({
      username: yup.string().required(t("This field is required")),
      nicNumber: yup.string().when("documentType", {
        is: commonConstants.DOCUMENT_TYPE_NATIONAL_ID,
        then: (schema) =>
          schema
            .matches(
              /^(\d{9}[VvXx]|\d{12})$/,
              "NIC number must be in the correct format (9 digits + X/V or 12 digits)",
            )
            .min(10, "NIC number must be minimum 10 characters long")
            .max(12, "NIC number must not exceed 12 digits")
            .required(t("This field is required")),
        otherwise: (schema) => schema.notRequired(),
      }),

      passportNumber: yup.string().when("documentType", {
        is: commonConstants.DOCUMENT_TYPE_PASSPORT_ID,
        then: (schema) =>
          schema
            .matches(
              /^[A-Za-z0-9]{6,12}$/,
              "Please enter a valid passport number.",
            )
            .required(t("This field is required")),
        otherwise: (schema) => schema.notRequired(),
      }),
    });

  const formRef = useRef(null);
  const [isNextEnabled, setIsNextEnabled] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [nicNumberError, setNicNumberError] = useState(false);
  const [passportNumberError, setPassportNumberError] = useState(false);
  const [userData, setUserData] = useState(null);
  const dispatch = useDispatch();
  const onValidate = async (values) => {
    return await Validator(values, validateSchema);
  };

  const { auth, lang } = useSelector((state) => ({
    auth: state.auth,
    lang: state.lang,
  }));

  const forgotFormData = useSelector((state) => state.auth.forgotFormData);
  useEffect(() => {
    if (forgotFormData && formRef.current) {
      formRef.current.initialize(forgotFormData);
    }
  }, [forgotFormData]);

  const submit = (data) => {
    setUserData(data);
    dispatch(setForgotPasswordFormData(data));
    let payload = {
      personalIdTypeCode: data?.documentType,
      personalId:
        data?.documentType == commonConstants.DOCUMENT_TYPE_NATIONAL_ID
          ? data?.nicNumber
          : data?.passportNumber,
      username: data?.username,
      locale: lang?.defaultLang,
      setModalOpen,
    };

    dispatch(forgotPasswordValidateRequest(payload));
  };

  const otpConfirm = async (values) => {
    const fingerPrint = await getFingerPrint();

    const request = {
      ...values,
      uid: fingerPrint,
      username: userData?.username,
    };
    dispatch(forgotPasswordOTPConfirmRequest(request));
  };

  useEffect(() => {
    if (auth?.isForgotFlowResetSuccess) {
    }
  }, [auth?.isForgotFlowResetSuccess]);

  const otpResend = (sendType) => {
    dispatch(
      forgotPasswordOTPResendRequest({
        username: userData?.username,
        sendType: sendType,
      }),
    );
  };

  const closeOtpModal = () => {
    dispatch(clearForgotDetails());
    setModalOpen(false);
  };

  return (
    <>
      <Form
        onSubmit={submit}
        validate={onValidate}
        initialValues={{
          documentType: commonConstants.DOCUMENT_TYPE_NATIONAL_ID,
        }}
        render={({
          handleSubmit,
          values,
          submitting,
          hasValidationErrors,
          form,
        }) => {
          useEffect(() => {
            setIsNextEnabled(!hasValidationErrors);
          }, [hasValidationErrors]);

          useEffect(() => {
            const unsubscribe = form.subscribe(
              ({ dirty }) => {
                onDirtyChange?.(dirty);
              },
              { dirty: true },
            );

            return () => unsubscribe();
          }, [form]);

          formRef.current = form;
          return (
            <form>
              <UiInput
                name="username"
                id="username"
                label={`${t("Username")} *`}
                type="text"
                min={0}
                clearable={true}
                maxLength={20}
                form={form}
                onInput={(e) => {
                  e.target.value = e.target.value.replace(/[^a-zA-Z0-9]/g, "");
                }}
                value={values.username}
              />

              <UiSelectInput
                label={t("Identification Document Type")}
                id="documentType"
                name="documentType"
                form={form}
                value={values.documentType}
              >
                <MenuItem value={commonConstants.DOCUMENT_TYPE_NATIONAL_ID}>
                  {t("NIC")}
                </MenuItem>
                <MenuItem value={commonConstants.DOCUMENT_TYPE_PASSPORT_ID}>
                  {t("Passport")}
                </MenuItem>
              </UiSelectInput>

              {values.documentType ==
              commonConstants.DOCUMENT_TYPE_NATIONAL_ID ? (
                <UiInput
                  name="nicNumber"
                  id="inputNicNumber"
                  label={`${t("NIC Number")} *`}
                  type="text"
                  min={0}
                  clearable={true}
                  maxLength={12}
                  value={values.nicNumber}
                  inputonChange={(e) => {
                    const rawInput = e.target.value.replace(/\s/g, "");
                    let cleaned = rawInput.replace(/[^0-9VXvx]/g, "");
                    if (cleaned.length > 12) cleaned = cleaned.slice(0, 12);
                    cleaned = cleaned
                      .split("")
                      .filter((char, index) => {
                        if (index < 9) return /[0-9]/.test(char);
                        if (index === 9) return /[0-9VXvx]/.test(char);
                        if (index > 9) return /[0-9]/.test(char);
                        return false;
                      })
                      .join("");

                    const result = formatNicNumberInput(cleaned);

                    if (result.value === "" || result.isValid) {
                      setNicNumberError(false);
                    } else {
                      setNicNumberError(result.message);
                    }

                    form.change("nicNumber", result.value);
                  }}
                  error={nicNumberError}
                />
              ) : (
                <>
                  <UiInput
                    name="passportNumber"
                    id="inputPassportNumber"
                    label={`${t("Passport Number")} *`}
                    type="text"
                    min={0}
                    clearable={true}
                    maxLength={12}
                    form={form}
                    value={values.passportNumber}
                    inputonChange={(passportNumber) => {
                      const digits = passportNumber.target.value
                        .replace(/[^0-9A-Za-z]/g, "")
                        .slice(0, 12);

                      const result = validPassportNumber(digits);
                      if (digits === "" || result.isValid) {
                        setPassportNumberError(false);
                      } else {
                        setPassportNumberError(result.message);
                      }
                      form.change("passportNumber", digits);
                    }}
                    error={passportNumberError}
                    helperText={
                      passportNumberError ? passportNumberError : null
                    }
                  />
                </>
              )}

              <UiButton
                className="df-ui-dark-bg-btn w-100 mt-2"
                name={t("Next")}
                disabled={!isNextEnabled || submitting}
                onClick={handleSubmit}
              />
            </form>
          );
        }}
      />

      <OtpConfirmModal
        show={modalOpen}
        userDetails={auth?.forgotDetails?.customer}
        otpConfirmRequest={otpConfirm}
        otpResendRequest={otpResend}
        closeModal={closeOtpModal}
        countDownTimer={auth?.forgetPasswordOtpTimer}
      />
    </>
  );
};
